/** The person's scheduled bot runs across every bot, inside QuantisAI: where
 * a member (who has no bot settings screen) sets up and manages them. Hidden
 * while loading, when the feature is off, or when loading fails; shown with
 * no schedules once enabled, because this is where the first one is made. */
import { useState } from 'react';
import { Button } from '../../shared/ui/button';
import type { BotSchedule } from '../../services/BotTaskSchedulesService';
import type { BotTask } from '../../services/BotTasksService';
import { BotScheduleForm } from '../workspaces/bots/BotScheduleForm';
import { BotScheduleRow } from '../workspaces/bots/BotScheduleRow';
import { ScheduleDeleteDialog } from '../workspaces/bots/ScheduleDeleteDialog';
import { useBotSchedules } from '../workspaces/bots/useBotSchedules';
import { useWorkspaceBots } from './SIBotMentionMenu';

/** `onRan` fires when Run now starts a task, so the bot task list refetches. */
export function SIBotSchedules({ workspaceId, onRan }: { workspaceId: string; onRan: (task: BotTask) => void }) {
  const { schedules, enabled, loaded, upsert, drop } = useBotSchedules(workspaceId, 'mine');
  const bots = useWorkspaceBots(workspaceId);
  const [editing, setEditing] = useState<BotSchedule | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<BotSchedule | null>(null);
  if (!loaded || !enabled) return null;

  return (
    <section aria-label="Scheduled runs" className="mb-4 space-y-2">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-xs font-medium text-muted-foreground">Scheduled runs</p>
        {bots.length > 0 && <Button size="sm" variant="outline" className="h-7" onClick={() => { setEditing(null); setFormOpen(true); }}>Run on a schedule</Button>}
      </div>
      {!schedules.length && <p className="text-xs text-muted-foreground">No schedules yet.</p>}
      {schedules.map((schedule) => (
        <BotScheduleRow key={schedule.id} schedule={schedule} showBot onChange={upsert} onRan={onRan} onDelete={setDeleting}
          onEdit={(item) => { setEditing(item); setFormOpen(true); }} />
      ))}

      <BotScheduleForm key={editing?.id ?? 'new'} open={formOpen} bots={bots} workspaceId={workspaceId} schedule={editing}
        onClose={() => setFormOpen(false)} onSaved={upsert} />

      <ScheduleDeleteDialog schedule={deleting} onClose={() => setDeleting(null)} onDeleted={(schedule) => drop(schedule.id)} />
    </section>
  );
}
